import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import {
  User,
  Phone,
  MapPin,
  FileText,
  Edit,
  Save,
  X,
  Upload,
  Shield,
  Key,
  Eye,
  EyeOff,
  Pencil,
  BadgeCheck,
  Info,
  Package,
  Truck,
  ClipboardList,
  Wallet,
  ChevronRight,
  Mail,
  CreditCard,
  Clock,
} from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import API_BASE from '../services/apiBase';
import '../styles/profile-page.css';

function formatShipmentStatusLabel(status) {
  const map = {
    pending: 'Pending',
    processing: 'Processing',
    'picked-up': 'Picked Up',
    'in-transit': 'In Transit',
    delivered: 'Delivered',
    exception: 'Exception',
  };
  return map[String(status || '').toLowerCase()] || status || '—';
}

function formatProfileDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function mapShipmentRow(s) {
  return {
    id: s.trackingNumber,
    status: formatShipmentStatusLabel(s.status),
    origin: [s.origin?.city, s.origin?.country].filter(Boolean).join(', ') || '—',
    destination: [s.destination?.city, s.destination?.country].filter(Boolean).join(', ') || '—',
    service: s.service?.name || s.service?.type || 'Cargo',
    boxType: 'Shipment',
    scheduleLabel: s.actualDelivery ? 'Delivered on' : 'Estimated delivery',
    scheduleValue: formatProfileDate(s.actualDelivery || s.estimatedDelivery),
    updated: formatProfileDate(s.updatedAt),
    pill: s.status === 'delivered' ? 'done' : 'transit',
  };
}

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: ctxUser, login } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [user, setUser] = useState(null);
  const [memberSinceLabel, setMemberSinceLabel] = useState('—');
  const [formData, setFormData] = useState({
    fullName: '',
    nameWithInitials: '',
    phoneNumber: '',
    email: '',
    address: '',
    passportNumber: '',
    nicNumber: '',
    idType: 'passport',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    passportPhoto: null,
    nicFront: null,
    nicBack: null,
  });
  const [errors, setErrors] = useState({});
  const [shipmentStats, setShipmentStats] = useState({ total: 0, inTransit: 0, delivered: 0 });
  const [recentShipments, setRecentShipments] = useState([]);
  const [billing, setBilling] = useState({ totalSpent: 0, outstandingBalance: 0, receipts: [], message: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  const profileStats = [
    { Icon: Package, value: String(shipmentStats.total), label: 'Total Shipments', link: '/tracking', linkText: 'Track' },
    { Icon: Truck, value: String(shipmentStats.inTransit), label: 'In Transit', link: '/tracking', linkText: 'Track' },
    { Icon: ClipboardList, value: String(shipmentStats.delivered), label: 'Delivered', link: '/tracking', linkText: 'Track' },
    {
      Icon: Wallet,
      value: billing.totalSpent > 0 ? `RM ${billing.totalSpent.toFixed(2)}` : '—',
      label: billing.outstandingBalance > 0 ? `Owed RM ${billing.outstandingBalance.toFixed(2)}` : 'Total spent',
      link: '/contact',
      linkText: billing.receipts.length ? 'Billing' : 'Contact us',
    },
  ];

  useEffect(() => {
    let activeUser = null;
    if (location.state?.user?.isLoggedIn) {
      activeUser = location.state.user;
      setUser(activeUser);
      login(activeUser);
    } else if (ctxUser?.isLoggedIn) {
      activeUser = ctxUser;
      setUser(ctxUser);
    } else {
      try {
        const saved = localStorage.getItem('jet_user');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.isLoggedIn) {
            activeUser = parsed;
            setUser(parsed);
            login(parsed);
          }
        }
      } catch (_) {}
    }

    if (!activeUser) {
      navigate('/login');
      return;
    }

    const msKey = `jet_member_since_${activeUser.loginId}`;
    try {
      let raw = localStorage.getItem(msKey);
      if (!raw) {
        raw = new Date().toISOString();
        localStorage.setItem(msKey, raw);
      }
      const d = new Date(raw);
      setMemberSinceLabel(
        Number.isFinite(d.getTime())
          ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '—'
      );
    } catch (_) {
      setMemberSinceLabel('—');
    }

    try {
      let storedProfile = localStorage.getItem(`jet_profile_${activeUser.loginId}`);
      if (storedProfile) {
        const parsedProfile = JSON.parse(storedProfile);
        setFormData((prev) => ({ ...prev, ...parsedProfile }));
      } else {
        const inferred = {
          idType: activeUser.loginType === 'passport' ? 'passport' : 'passport',
          email: activeUser.loginType === 'email' ? activeUser.loginId : '',
          passportNumber: activeUser.loginType === 'passport' ? activeUser.loginId : '',
        };
        setFormData((prev) => ({ ...prev, ...inferred }));
      }
    } catch (_) {}
  }, [location.state, navigate, ctxUser, login]);

  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      try {
        const headers = { Authorization: `Bearer ${user.token}` };
        const [meRes, shipRes, billRes] = await Promise.all([
          fetch(`${API_BASE}/api/auth/me`, { headers }),
          fetch(`${API_BASE}/api/auth/my-shipments`, { headers }),
          fetch(`${API_BASE}/api/auth/my-billing`, { headers }),
        ]);
        const meJson = await meRes.json().catch(() => ({}));
        const shipJson = await shipRes.json().catch(() => ({}));
        const billJson = await billRes.json().catch(() => ({}));
        if (!cancelled && meRes.ok && meJson.data) {
          const u = meJson.data;
          setFormData((prev) => ({
            ...prev,
            fullName: u.name || prev.fullName,
            phoneNumber: u.phone || prev.phoneNumber,
            email: u.email || prev.email,
            address: prev.address,
            passportNumber: u.passportNumber || prev.passportNumber,
            nicNumber: u.nicNumber || prev.nicNumber,
            idType: u.passportNumber ? 'passport' : u.nicNumber ? 'nic' : prev.idType,
          }));
          if (u.createdAt) {
            const d = new Date(u.createdAt);
            setMemberSinceLabel(
              Number.isFinite(d.getTime())
                ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—',
            );
          }
        }
        if (!cancelled && shipRes.ok) {
          setShipmentStats(shipJson.stats || { total: 0, inTransit: 0, delivered: 0 });
          setRecentShipments((shipJson.data || []).map(mapShipmentRow));
        }
        if (!cancelled && billRes.ok && billJson.data) {
          setBilling({
            totalSpent: Number(billJson.data.totalSpent) || 0,
            outstandingBalance: Number(billJson.data.outstandingBalance) || 0,
            receipts: billJson.data.receipts || [],
            message: billJson.data.message || '',
          });
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.token]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.nameWithInitials.trim()) newErrors.nameWithInitials = 'Name with initials is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';

    if (formData.idType === 'passport') {
      if (!formData.passportNumber.trim()) newErrors.passportNumber = 'Passport number is required';
    } else if (!formData.nicNumber.trim()) {
      newErrors.nicNumber = 'NIC number is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        if (user?.token) {
          const res = await fetch(`${API_BASE}/api/auth/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${user.token}`,
            },
            body: JSON.stringify({
              name: formData.fullName.trim(),
              phone: formData.phoneNumber.trim(),
              passportNumber: formData.idType === 'passport' ? formData.passportNumber.trim() : '',
              nicNumber: formData.idType === 'nic' ? formData.nicNumber.trim() : '',
            }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(json.message || 'Failed to save profile');
          if (formData.idType === 'passport' || formData.idType === 'nic') {
            const billRes = await fetch(`${API_BASE}/api/auth/my-billing`, {
              headers: { Authorization: `Bearer ${user.token}` },
            });
            const billJson = await billRes.json().catch(() => ({}));
            if (billRes.ok && billJson.data) {
              setBilling({
                totalSpent: Number(billJson.data.totalSpent) || 0,
                outstandingBalance: Number(billJson.data.outstandingBalance) || 0,
                receipts: billJson.data.receipts || [],
                message: billJson.data.message || '',
              });
            }
          }
        }
        setIsEditing(false);
        try {
          if (user?.loginId) {
            localStorage.setItem(`jet_profile_${user.loginId}`, JSON.stringify(formData));
          }
        } catch (_) {}
      } catch (err) {
        setErrors({ general: err.message || 'Failed to save profile' });
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!passwordData.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!passwordData.newPassword) newErrors.newPassword = 'New password is required';
    if (passwordData.newPassword.length < 6) newErrors.newPassword = 'Password must be at least 6 characters';
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        const res = await fetch(`${API_BASE}/api/auth/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || 'Failed to change password');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordChange(false);
      } catch (err) {
        setErrors({ currentPassword: err.message || 'Failed to change password' });
      }
    }
  };

  const handleFileUpload = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, [fileType]: 'Please upload an image file' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, [fileType]: 'File size must be less than 5MB' }));
        return;
      }
      setUploadedFiles((prev) => ({ ...prev, [fileType]: file }));
      setErrors((prev) => ({ ...prev, [fileType]: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const displayEmail = formData.email.trim() || (user?.loginType === 'email' ? user.loginId : '—');
  const displayPhone = formData.phoneNumber.trim() || '—';
  const profileHeading =
    formData.fullName.trim() ||
    (user?.loginType === 'email' && String(user?.loginId || '').includes('@') ? 'My profile' : user?.loginId) ||
    'User';
  const avatarLetter = (
    formData.fullName.trim() ||
    (typeof displayEmail === 'string' && displayEmail !== '—' ? displayEmail : '') ||
    user?.loginId ||
    'U'
  )
    .charAt(0)
    .toUpperCase();
  const locationLine =
    formData.address
      ?.split('\n')
      .map((s) => s.trim())
      .filter(Boolean)[0] ||
    formData.address?.trim() ||
    'Add your address';
  const verified = Boolean(formData.email?.trim() && formData.fullName?.trim());

  if (!user) {
    return (
      <div className="prof-page">
        <div className="prof-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="prof-page">
      <section className="prof-hero" aria-labelledby="prof-hero-title">
        <div className="prof-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="prof-hero__overlay" />
        <div className="prof-hero__inner">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="prof-hero__crumb">
              <Link to="/">Home</Link>
              <span aria-hidden> &gt; </span>
              My Profile
            </p>
            <h1 id="prof-hero-title" className="prof-hero__title">
              My Profile
            </h1>
          </motion.div>
        </div>
      </section>

      <div className="prof-wrap">
        <motion.article
          className="prof-summary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="prof-summary__left">
            <div className="prof-avatar-wrap">
              <div className="prof-avatar" aria-hidden>
                {avatarLetter}
              </div>
              <button type="button" className="prof-avatar-edit" onClick={handleEdit} aria-label="Edit profile">
                <Pencil size={16} strokeWidth={2.2} />
              </button>
            </div>
            <div className="prof-summary__meta">
              <h2 className="prof-summary__title">
                {profileHeading}
                {verified && (
                  <span className="prof-badge-verified">
                    <BadgeCheck size={12} strokeWidth={2.5} aria-hidden />
                    Verified
                  </span>
                )}
              </h2>
              <ul className="prof-summary__lines">
                <li>
                  <Mail size={16} strokeWidth={2} aria-hidden />
                  <span>{displayEmail}</span>
                </li>
                <li>
                  <Phone size={16} strokeWidth={2} aria-hidden />
                  <span>{displayPhone}</span>
                </li>
                <li>
                  <MapPin size={16} strokeWidth={2} aria-hidden />
                  <span>{locationLine}</span>
                </li>
                <li>
                  <Clock size={16} strokeWidth={2} aria-hidden />
                  <span>Member since {memberSinceLabel}</span>
                </li>
              </ul>
              <div className="prof-summary__actions">
                {!isEditing ? (
                  <>
                    <button type="button" className="prof-btn-outline" onClick={handleEdit}>
                      <Edit size={16} />
                      Edit profile
                    </button>
                    <button type="button" className="prof-btn-solid" onClick={() => setShowPasswordChange(true)}>
                      <Key size={16} />
                      Change password
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="prof-btn-solid" onClick={handleSave}>
                      <Save size={16} />
                      Save
                    </button>
                    <button type="button" className="prof-btn-outline" onClick={handleCancel}>
                      <X size={16} />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="prof-stats">
            {profileStats.map((s, i) => {
              const Icon = s.Icon;
              const tones = ['blue', 'green', 'purple', 'orange'];
              const tone = tones[i % tones.length];
              return (
                <div key={s.label} className="prof-stat">
                  <div className={`prof-stat__icon prof-stat__icon--${tone}`}>
                    <Icon size={20} strokeWidth={2} aria-hidden />
                  </div>
                  <span className="prof-stat__num">{s.value}</span>
                  <span className="prof-stat__label">{s.label}</span>
                  <Link to={s.link} className="prof-stat__link">
                    {s.linkText}
                    <ChevronRight size={14} aria-hidden />
                  </Link>
                </div>
              );
            })}
          </div>
        </motion.article>

        <div className="prof-grid prof-grid--3">
          <section className="prof-card">
            <div className="prof-card__head">
              <h2 className="prof-card__head-title">
                <User size={18} strokeWidth={2} aria-hidden />
                Personal information
              </h2>
              {!isEditing ? (
                <button type="button" className="prof-btn-outline" onClick={handleEdit}>
                  <Edit size={14} />
                  Edit
                </button>
              ) : (
                <span style={{ display: 'flex', gap: '0.35rem' }}>
                  <button type="button" className="prof-btn-solid" onClick={handleSave}>
                    <Save size={14} />
                    Save
                  </button>
                  <button type="button" className="prof-btn-outline" onClick={handleCancel}>
                    <X size={14} />
                  </button>
                </span>
              )}
            </div>
            <div className="prof-card__body">
              {!isEditing ? (
                <ul className="prof-kv">
                  <li>
                    <span>Full name</span>
                    <span>{formData.fullName || '—'}</span>
                  </li>
                  <li>
                    <span>Name with initials</span>
                    <span>{formData.nameWithInitials || '—'}</span>
                  </li>
                  <li>
                    <span>Email</span>
                    <span>{formData.email || '—'}</span>
                  </li>
                  <li>
                    <span>Phone</span>
                    <span>{formData.phoneNumber || '—'}</span>
                  </li>
                  <li>
                    <span>Company name</span>
                    <span>—</span>
                  </li>
                  <li>
                    <span>Member since</span>
                    <span>{memberSinceLabel}</span>
                  </li>
                  <li>
                    <span>Preferred language</span>
                    <span>English</span>
                  </li>
                </ul>
              ) : (
                <div className="prof-form-grid">
                  <div>
                    <label className="form-label" htmlFor="pf-fullName">
                      Full name
                    </label>
                    <input
                      id="pf-fullName"
                      name="fullName"
                      className={`form-input ${errors.fullName ? 'error' : ''}`}
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Full name"
                    />
                    {errors.fullName && <span className="error-text">{errors.fullName}</span>}
                  </div>
                  <div>
                    <label className="form-label" htmlFor="pf-initials">
                      Name with initials
                    </label>
                    <input
                      id="pf-initials"
                      name="nameWithInitials"
                      className={`form-input ${errors.nameWithInitials ? 'error' : ''}`}
                      value={formData.nameWithInitials}
                      onChange={handleInputChange}
                      placeholder="e.g. J. Doe"
                    />
                    {errors.nameWithInitials && <span className="error-text">{errors.nameWithInitials}</span>}
                  </div>
                  <div>
                    <label className="form-label" htmlFor="pf-email">
                      Email
                    </label>
                    <input
                      id="pf-email"
                      name="email"
                      type="email"
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email"
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  <div>
                    <label className="form-label" htmlFor="pf-phone">
                      Phone
                    </label>
                    <input
                      id="pf-phone"
                      name="phoneNumber"
                      className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="Phone"
                    />
                    {errors.phoneNumber && <span className="error-text">{errors.phoneNumber}</span>}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="prof-card">
            <div className="prof-card__head">
              <h2 className="prof-card__head-title">
                <MapPin size={18} strokeWidth={2} aria-hidden />
                Default shipping address
              </h2>
              <button type="button" className="prof-btn-outline" onClick={handleEdit}>
                Manage
              </button>
            </div>
            <div className="prof-card__body">
              <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: 'var(--prof-navy)', fontSize: '0.9rem' }}>
                {profileHeading}
              </p>
              {isEditing ? (
                <>
                  <label className="form-label" htmlFor="pf-address">
                    Full address
                  </label>
                  <textarea
                    id="pf-address"
                    name="address"
                    className={`form-textarea ${errors.address ? 'error' : ''}`}
                    rows={4}
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Street, city, postal code, country"
                  />
                  {errors.address && <span className="error-text">{errors.address}</span>}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.55, color: 'var(--prof-muted)' }}>
                  {formData.address?.trim() || 'No default address on file.'}
                </p>
              )}
              <div className="prof-alert">
                <Info size={16} strokeWidth={2} aria-hidden />
                <span>This address is used as your default shipping address for shipments where applicable.</span>
              </div>
            </div>
          </section>

          <section className="prof-card">
            <div className="prof-card__head">
              <h2 className="prof-card__head-title">
                <CreditCard size={18} strokeWidth={2} aria-hidden />
                Billing &amp; receipts
              </h2>
              <Link to="/contact" className="prof-activity__head-link">
                Billing help
                <ChevronRight size={16} strokeWidth={2} aria-hidden />
              </Link>
            </div>
            <div className="prof-card__body">
              {billing.message && !billing.receipts.length ? (
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--prof-muted)' }}>{billing.message}</p>
              ) : null}
              {billing.receipts.length ? (
                <ul className="prof-shipments-list" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {billing.receipts.slice(0, 6).map((r) => (
                    <li key={r.collectionNo} className="prof-shipment-card" style={{ marginBottom: '0.5rem' }}>
                      <div className="prof-shipment-card__top">
                        <div className="prof-shipment-card__lead">
                          <div className="prof-shipment-card__id">{r.collectionNo}</div>
                          <div className="prof-shipment-card__service">
                            Total RM {Number(r.total).toFixed(2)} · Paid RM {Number(r.paid).toFixed(2)}
                          </div>
                        </div>
                        <span className={`prof-pill prof-pill--${r.status === 'paid' ? 'done' : 'transit'}`}>
                          {r.balance > 0 ? `Balance RM ${Number(r.balance).toFixed(2)}` : 'Paid'}
                        </span>
                      </div>
                      <div className="prof-shipment-card__meta">
                        <span>{formatProfileDate(r.date)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--prof-muted)' }}>
                  No collection receipts linked yet. Add your passport or NIC above to match warehouse records.
                </p>
              )}
            </div>
          </section>
        </div>

        <section className="prof-card prof-card--shipments">
          <div className="prof-card__head">
            <h2 className="prof-card__head-title">
              <Package size={18} strokeWidth={2} aria-hidden />
              Recent shipments
            </h2>
            <Link to="/tracking" className="prof-activity__head-link">
              Open tracking
              <ChevronRight size={16} strokeWidth={2} aria-hidden />
            </Link>
          </div>
          <div className="prof-card__body prof-card__body--flush-list">
            <ul className="prof-shipments-list">
              {profileLoading && !recentShipments.length ? (
                <li className="prof-shipment-card" style={{ padding: '1rem' }}>Loading shipments…</li>
              ) : !recentShipments.length ? (
                <li className="prof-shipment-card" style={{ padding: '1rem' }}>No shipments linked to your account yet.</li>
              ) : recentShipments.map((row) => (
                <li key={row.id}>
                  <Link to={`/tracking?n=${encodeURIComponent(row.id)}`} className="prof-shipment-card">
                    <div className="prof-shipment-card__top">
                      <div className="prof-shipment-card__lead">
                        <div className="prof-shipment-card__id">{row.id}</div>
                        <div className="prof-shipment-card__service">
                          {row.service} · {row.boxType}
                        </div>
                      </div>
                      <span className={`prof-pill prof-pill--${row.pill}`}>{row.status}</span>
                    </div>
                    <dl className="prof-shipment-meta">
                      <div>
                        <dt>Origin</dt>
                        <dd>{row.origin}</dd>
                      </div>
                      <div>
                        <dt>Destination</dt>
                        <dd>{row.destination}</dd>
                      </div>
                      <div>
                        <dt>{row.scheduleLabel}</dt>
                        <dd>{row.scheduleValue}</dd>
                      </div>
                    </dl>
                    <div className="prof-shipment-card__foot">Last update · {row.updated}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="prof-card prof-account-extra">
          <div className="prof-card__head">
            <h2 className="prof-card__head-title">
              <Shield size={18} strokeWidth={2} aria-hidden />
              Account &amp; identity
            </h2>
          </div>
          <div className="prof-card__body">
            <div className="prof-subsection">
              <h3>
                <FileText size={16} aria-hidden />
                Account details
              </h3>
              <ul className="prof-kv">
                <li>
                  <span>Login ID</span>
                  <span>{user.loginId}</span>
                </li>
                <li>
                  <span>Account type</span>
                  <span>{user.loginType === 'email' ? 'Email' : 'Passport'}</span>
                </li>
              </ul>
            </div>

            <div className="prof-subsection">
              <h3>
                <FileText size={16} aria-hidden />
                Identity document
              </h3>
              {isEditing ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="form-label">ID type</label>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.35rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="idType"
                          value="passport"
                          checked={formData.idType === 'passport'}
                          onChange={handleInputChange}
                        />
                        Passport
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input type="radio" name="idType" value="nic" checked={formData.idType === 'nic'} onChange={handleInputChange} />
                        NIC
                      </label>
                    </div>
                  </div>

                  {formData.idType === 'passport' ? (
                    <div>
                      <div style={{ marginBottom: '1rem' }}>
                        <label className="form-label" htmlFor="pf-pass">
                          Passport number
                        </label>
                        <input
                          id="pf-pass"
                          type="text"
                          name="passportNumber"
                          value={formData.passportNumber}
                          onChange={handleInputChange}
                          className={`form-input ${errors.passportNumber ? 'error' : ''}`}
                          placeholder="Passport number"
                        />
                        {errors.passportNumber && <span className="error-text">{errors.passportNumber}</span>}
                      </div>
                      <div>
                        <label className="form-label">Passport photo</label>
                        <div
                          style={{
                            border: '2px dashed #d1d5db',
                            borderRadius: '8px',
                            padding: '1rem',
                            textAlign: 'center',
                            marginTop: '0.35rem',
                          }}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'passportPhoto')}
                            style={{ display: 'none' }}
                            id="passportPhoto"
                          />
                          <label htmlFor="passportPhoto" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                            <Upload className="w-6 h-6 text-gray-400" />
                            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                              {uploadedFiles.passportPhoto ? uploadedFiles.passportPhoto.name : 'Upload passport photo'}
                            </span>
                          </label>
                        </div>
                        {errors.passportPhoto && <span className="error-text">{errors.passportPhoto}</span>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: '1rem' }}>
                        <label className="form-label" htmlFor="pf-nic">
                          NIC number
                        </label>
                        <input
                          id="pf-nic"
                          type="text"
                          name="nicNumber"
                          value={formData.nicNumber}
                          onChange={handleInputChange}
                          className={`form-input ${errors.nicNumber ? 'error' : ''}`}
                          placeholder="NIC number"
                        />
                        {errors.nicNumber && <span className="error-text">{errors.nicNumber}</span>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label className="form-label">NIC front</label>
                          <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '0.35rem' }}>
                            <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'nicFront')} style={{ display: 'none' }} id="nicFront" />
                            <label htmlFor="nicFront" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                              <Upload className="w-5 h-5 text-gray-400" />
                              <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{uploadedFiles.nicFront ? uploadedFiles.nicFront.name : 'NIC front'}</span>
                            </label>
                          </div>
                          {errors.nicFront && <span className="error-text">{errors.nicFront}</span>}
                        </div>
                        <div>
                          <label className="form-label">NIC back</label>
                          <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginTop: '0.35rem' }}>
                            <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'nicBack')} style={{ display: 'none' }} id="nicBack" />
                            <label htmlFor="nicBack" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                              <Upload className="w-5 h-5 text-gray-400" />
                              <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{uploadedFiles.nicBack ? uploadedFiles.nicBack.name : 'NIC back'}</span>
                            </label>
                          </div>
                          {errors.nicBack && <span className="error-text">{errors.nicBack}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <ul className="prof-kv">
                  <li>
                    <span>ID type</span>
                    <span>{formData.idType === 'passport' ? 'Passport' : 'NIC'}</span>
                  </li>
                  <li>
                    <span>{formData.idType === 'passport' ? 'Passport number' : 'NIC number'}</span>
                    <span>{formData.idType === 'passport' ? formData.passportNumber || '—' : formData.nicNumber || '—'}</span>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </section>
      </div>

      {showPasswordChange && (
        <motion.div
          className="prof-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="presentation"
          onClick={() => setShowPasswordChange(false)}
        >
          <motion.div
            className="prof-modal"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="prof-pw-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 id="prof-pw-title" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--prof-navy)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={22} color="var(--prof-blue)" aria-hidden />
                Change password
              </h2>
              <button type="button" onClick={() => setShowPasswordChange(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.35rem' }} aria-label="Close">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label" htmlFor="pw-cur">
                  Current password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="pw-cur"
                    type={showPasswords.current ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={`form-input ${errors.currentPassword ? 'error' : ''}`}
                    placeholder="Current password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}
                    aria-label="Toggle visibility"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
                {errors.currentPassword && <span className="error-text">{errors.currentPassword}</span>}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label" htmlFor="pw-new">
                  New password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="pw-new"
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={`form-input ${errors.newPassword ? 'error' : ''}`}
                    placeholder="New password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}
                    aria-label="Toggle visibility"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
                {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label className="form-label" htmlFor="pw-conf">
                  Confirm new password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="pw-conf"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}
                    aria-label="Toggle visibility"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>

              <div style={{ display: 'flex', gap: '0.65rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowPasswordChange(false)} className="prof-btn-outline">
                  Cancel
                </button>
                <button type="submit" className="prof-btn-solid">
                  <Key size={16} />
                  Update password
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Profile;
